import * as React from "react";

export interface KomalogPreviewViewerProps {
    output: string;
}

// --- XML-Parsing ---

function getText(el: Element | Document, tag: string): string {
    return el.querySelector(tag)?.textContent?.trim() ?? "";
}

function getAll(el: Element | Document, tag: string): Element[] {
    return Array.from(el.querySelectorAll(tag));
}

interface KAddress {
    qualifier: string;
    matchcode?: string;
    name1?: string;
    name2?: string;
    street?: string;
    countryCode?: string;
    postalCode?: string;
    place?: string;
}

interface KSpaceReq {
    palletSpaceRequired?: string;
    loadingMeters?: string;
    cubicMeters?: string;
    length?: string;
    width?: string;
    height?: string;
}

interface KShipment {
    position: string;
    marksAndNumbers: string;
    content: string;
    quantity: string;
    packagingCode: string;
    grossWeight: string;
    netWeight: string;
    stackFactor: string;
    spaceReq: KSpaceReq;
}

interface KReference {
    description: string;
    shipmentNumber: string;
}

interface KEdiText {
    textCode: string;
    additionalText: string;
}

interface KOrder {
    processType: string;
    termsOfDelivery: string;
    product: string;
    dueDate: string;
    dueDateUntil: string;
    loadingDate: string;
    loadingDateUntil: string;
    addresses: KAddress[];
    shipments: KShipment[];
    references: KReference[];
    ediTexts: KEdiText[];
    insuranceAmount?: string;
    insuranceCurrency?: string;
}

interface KTransportations {
    orders: KOrder[];
}

function parseAddress(el: Element): KAddress {
    return {
        qualifier:   getText(el, "qualifier"),
        matchcode:   getText(el, "matchcode") || undefined,
        name1:       getText(el, "name1") || undefined,
        name2:       getText(el, "name2") || undefined,
        street:      getText(el, "street") || undefined,
        countryCode: getText(el, "countryCode") || undefined,
        postalCode:  getText(el, "postalCode") || undefined,
        place:       getText(el, "place") || undefined,
    };
}

function parseShipment(el: Element): KShipment {
    const sr = el.querySelector("spaceRequirements");
    const dims = sr?.querySelector("dimensions");
    return {
        position:       getText(el, "position"),
        marksAndNumbers: getText(el, "marksAndNumbers"),
        content:        getText(el, "content"),
        quantity:       getText(el, "quantity"),
        packagingCode:  getText(el, "packagingCode"),
        grossWeight:    getText(el, "grossWeight"),
        netWeight:      getText(el, "netWeight"),
        stackFactor:    getText(el, "stackFactor"),
        spaceReq: {
            palletSpaceRequired: sr ? getText(sr, "palletSpaceRequired") : undefined,
            loadingMeters:       sr ? getText(sr, "loadingMeters") : undefined,
            cubicMeters:         sr ? getText(sr, "cubicMeters") : undefined,
            length:              dims ? getText(dims, "length") : undefined,
            width:               dims ? getText(dims, "width") : undefined,
            height:              dims ? getText(dims, "height") : undefined,
        },
    };
}

function parseKomalogXml(xml: string): KTransportations | null {
    try {
        const doc = new DOMParser().parseFromString(xml, "application/xml");
        if (doc.querySelector("parseerror")) return null;
        if (!doc.querySelector("transportations")) return null;

        const orders = getAll(doc, "orders > order").map(ord => {
            const ins = ord.querySelector("insurance");
            return {
                processType:      getText(ord, "processType"),
                termsOfDelivery:  getText(ord, "termsOfDelivery"),
                product:          getText(ord, "product"),
                dueDate:          getText(ord, "dueDate"),
                dueDateUntil:     getText(ord, "dueDateUntil"),
                loadingDate:      getText(ord, "loadingDate"),
                loadingDateUntil: getText(ord, "loadingDateUntil"),
                addresses:        getAll(ord, "addresses > address").map(parseAddress),
                shipments:        getAll(ord, "shipments > shipment").map(parseShipment),
                references:       getAll(ord, "references > referenceNumber").map(r => ({
                    description:    getText(r, "description"),
                    shipmentNumber: getText(r, "shipmentNumber"),
                })),
                ediTexts:         getAll(ord, "ediTexts > ediText").map(e => ({
                    textCode:       getText(e, "textCode"),
                    additionalText: getText(e, "additionalText"),
                })),
                insuranceAmount:   ins ? getText(ins, "amount") : undefined,
                insuranceCurrency: ins ? getText(ins, "currencySymbol") : undefined,
            } as KOrder;
        });

        return { orders };
    } catch { return null; }
}

export function parseKomalogData(output: string): KTransportations | null {
    if (!output) return null;
    const trimmed = output.trim();
    if (!trimmed.startsWith("<")) return null;
    return parseKomalogXml(trimmed);
}

// --- Hilfsfunktionen ---

function fmtDateTime(iso: string): string {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const dd  = String(d.getDate()).padStart(2, "0");
        const mm  = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        const hh  = String(d.getHours()).padStart(2, "0");
        const min = String(d.getMinutes()).padStart(2, "0");
        return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
    } catch { return iso; }
}

const QUALIFIER_LABEL: Record<string, string> = {
    SHP: "Absender",
    CON: "Empfänger",
    INV: "Rechnungsempf.",
    BY:  "Auftraggeber",
    DEL: "Abw. Empfangsort",
};

// --- Styles ---

const S = {
    root: {
        fontFamily: "Segoe UI, Tahoma, Arial, sans-serif",
        fontSize: 11,
        background: "#e8e8e8",
        padding: 6,
        overflowY: "auto" as const,
        height: "100%",
        boxSizing: "border-box" as const,
    },
    orderBox: {
        background: "#f0f0f0",
        border: "1px solid #b0b0b0",
        padding: 6,
        marginBottom: 8,
    },
    sectionTitle: {
        background: "#d0d8e8",
        border: "1px solid #a0aec0",
        padding: "1px 6px",
        fontWeight: "bold" as const,
        fontSize: 11,
        margin: "4px 0 3px",
    },
    row: {
        display: "flex",
        flexWrap: "wrap" as const,
        alignItems: "center",
        gap: 4,
        marginBottom: 2,
    },
    label: {
        fontSize: 11,
        color: "#333",
        whiteSpace: "nowrap" as const,
        marginRight: 2,
    },
    labelBold: {
        fontSize: 11,
        fontWeight: "bold" as const,
        color: "#333",
        whiteSpace: "nowrap" as const,
        marginRight: 2,
    },
    input: (width?: number, highlight?: boolean): React.CSSProperties => ({
        border: "1px solid #aaa",
        background: highlight ? "#ffe066" : "#fff",
        padding: "1px 3px",
        minHeight: 18,
        width: width ?? undefined,
        minWidth: width ? undefined : 60,
        fontSize: 11,
        display: "inline-flex",
        alignItems: "center",
        whiteSpace: "nowrap" as const,
        overflow: "hidden",
    }),
    divider: {
        borderTop: "1px solid #c0c0c0",
        margin: "4px 0",
    },
    th: {
        border: "1px solid #aaa",
        background: "#003080",
        color: "#fff",
        padding: "1px 5px",
        fontWeight: "normal" as const,
        fontSize: 11,
        whiteSpace: "nowrap" as const,
        textAlign: "left" as const,
    },
    td: {
        border: "1px solid #ccc",
        padding: "1px 5px",
        fontSize: 11,
        whiteSpace: "nowrap" as const,
    },
};

function KField({ label, value, width, highlight, bold }: {
    label: string; value?: string; width?: number; highlight?: boolean; bold?: boolean;
}) {
    return (
        <>
            <span style={bold ? S.labelBold : S.label}>{label}</span>
            <div style={S.input(width, highlight)}>{value ?? ""}</div>
        </>
    );
}

function AddressRow({ addr }: { addr: KAddress }) {
    const label = QUALIFIER_LABEL[addr.qualifier] ?? addr.qualifier;
    const hasDetail = addr.name1 || addr.street || addr.place;
    return (
        <div style={S.row}>
            <span style={S.labelBold}>{label}:</span>
            {addr.matchcode && <div style={S.input(90, true)}>{addr.matchcode}</div>}
            {addr.name1 && <div style={S.input(180)}>{addr.name1}</div>}
            {addr.countryCode && <div style={S.input(24)}>{addr.countryCode}</div>}
            {addr.postalCode && <div style={S.input(50)}>{addr.postalCode}</div>}
            {addr.place && <div style={S.input(140)}>{addr.place}</div>}
            {addr.street && <div style={S.input(160)}>{addr.street}</div>}
            {!hasDetail && !addr.matchcode && <div style={S.input(120)} />}
        </div>
    );
}

function OrderView({ ord, index }: { ord: KOrder; index: number }) {
    const [ediOpen, setEdiOpen] = React.useState(false);

    const shp = ord.addresses.find(a => a.qualifier === "SHP");
    const con = ord.addresses.find(a => a.qualifier === "CON");
    const inv = ord.addresses.find(a => a.qualifier === "INV");
    const by  = ord.addresses.find(a => a.qualifier === "BY");
    const others = ord.addresses.filter(a => !["SHP","CON","INV","BY"].includes(a.qualifier));

    return (
        <div style={S.orderBox}>
            {/* Header-Zeile */}
            <div style={S.row}>
                <KField label="Beladedatum:"  value={fmtDateTime(ord.loadingDate)}      width={115} highlight />
                <KField label="bis:"           value={fmtDateTime(ord.loadingDateUntil)} width={115} highlight />
                <KField label="Entladedatum:" value={fmtDateTime(ord.dueDate)}           width={115} highlight />
                <KField label="bis:"           value={fmtDateTime(ord.dueDateUntil)}     width={115} highlight />
            </div>
            <div style={S.row}>
                <KField label="Produkt:" value={ord.product} width={160} highlight />
                <KField label="Frankatur:" value={ord.termsOfDelivery} width={50} highlight />
                <KField label="Prozesstyp:" value={ord.processType} width={30} />
                {ord.insuranceAmount && (
                    <KField label="Versicherung:" value={`${ord.insuranceAmount} ${ord.insuranceCurrency ?? ""}`} />
                )}
            </div>

            <div style={S.divider} />

            {/* Adressen */}
            {shp && <AddressRow addr={shp} />}
            {con && <AddressRow addr={con} />}
            {inv && <AddressRow addr={inv} />}
            {by  && <AddressRow addr={by} />}
            {others.map((a, i) => <AddressRow key={i} addr={a} />)}

            {/* Referenzen */}
            {ord.references.length > 0 && (
                <>
                    <div style={S.sectionTitle}>Referenzen</div>
                    <table style={{ borderCollapse: "collapse", fontSize: 11, background: "#fff" }}>
                        <thead>
                            <tr>
                                <th style={S.th}>Beschreibung</th>
                                <th style={S.th}>Sendungsnummer</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ord.references.map((r, i) => (
                                <tr key={i}>
                                    <td style={S.td}>{r.description}</td>
                                    <td style={S.td}>{r.shipmentNumber}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {/* Sendungen */}
            {ord.shipments.length > 0 && (
                <>
                    <div style={S.sectionTitle}>Sendungen</div>
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ borderCollapse: "collapse", fontSize: 11, background: "#fff", width: "100%" }}>
                            <thead>
                                <tr>
                                    {["Stapelfakt.", "Pos.", "Zeichen / Nummer", "Anzahl", "Verpackung",
                                      "Inhalt", "Bruttogew.", "Nettogewicht", "Pal.-Plätze", "LDM", "CBM",
                                      "Länge (cm)", "Breite (cm)", "Höhe (cm)"].map(h => (
                                        <th key={h} style={S.th}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {ord.shipments.map((s, i) => (
                                    <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f5f5ff" }}>
                                        <td style={S.td}>{s.stackFactor}</td>
                                        <td style={S.td}>{s.position}</td>
                                        <td style={S.td}>{s.marksAndNumbers}</td>
                                        <td style={S.td}>{s.quantity}</td>
                                        <td style={S.td}>{s.packagingCode}</td>
                                        <td style={S.td}>{s.content}</td>
                                        <td style={S.td}>{s.grossWeight ? `${s.grossWeight} kg` : ""}</td>
                                        <td style={S.td}>{s.netWeight ? `${s.netWeight} kg` : ""}</td>
                                        <td style={S.td}>{s.spaceReq.palletSpaceRequired}</td>
                                        <td style={S.td}>{s.spaceReq.loadingMeters}</td>
                                        <td style={S.td}>{s.spaceReq.cubicMeters}</td>
                                        <td style={S.td}>{s.spaceReq.length}</td>
                                        <td style={S.td}>{s.spaceReq.width}</td>
                                        <td style={S.td}>{s.spaceReq.height}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* DFÜ-Texte (collapsible) */}
            {ord.ediTexts.length > 0 && (
                <>
                    <div
                        onClick={() => setEdiOpen(o => !o)}
                        style={{ ...S.divider, cursor: "pointer", userSelect: "none",
                            display: "flex", alignItems: "center", gap: 4 }}
                    >
                        <span style={{
                            fontSize: 10, color: "#555", background: "#e8e8e8",
                            padding: "0 4px", marginTop: -7, position: "relative",
                        }}>
                            {ediOpen ? "▾" : "▸"} DFÜ-Texte ({ord.ediTexts.length})
                        </span>
                    </div>
                    {ediOpen && (
                        <table style={{ borderCollapse: "collapse", fontSize: 11, background: "#fff", width: "100%" }}>
                            <thead>
                                <tr>
                                    <th style={S.th}>Textcode</th>
                                    <th style={S.th}>Zusatztext</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ord.ediTexts.map((e, i) => (
                                    <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f5f5ff" }}>
                                        <td style={{ ...S.td, whiteSpace: "nowrap" }}>{e.textCode}</td>
                                        <td style={{ ...S.td, whiteSpace: "pre-wrap", maxWidth: 600 }}>{e.additionalText}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </>
            )}
        </div>
    );
}

export function KomalogPreviewViewer({ output }: KomalogPreviewViewerProps) {
    const data = React.useMemo(() => parseKomalogData(output), [output]);

    if (!data) {
        return (
            <div style={{ color: "#888", fontStyle: "italic", padding: 16, fontSize: 12,
                fontFamily: "Segoe UI, Tahoma, sans-serif" }}>
                Kein Komalog-XML erkannt. Erwartet wird ein XML mit &lt;transportations&gt;-Root.
            </div>
        );
    }

    return (
        <div style={S.root}>
            {data.orders.map((ord, i) => (
                <OrderView key={i} ord={ord} index={i} />
            ))}
        </div>
    );
}
